// © Microsoft Corporation. All rights reserved.

import { toNumber as _toNumber, isString } from "lodash-es";

export type KeyValuePair<K, V> = { key: K, value: V };
export type KeyValueArray<K, V> = KeyValuePair<K, V>[];

export namespace Utility {
    /**
     * Check if given value is not null. Returns true when not null, otherwise false.
     *
     * @export
     * @param {any} value - Value to check for not null
     * @return {boolean}
     */
    export function isNotNullOrUndefined(value: any): boolean {
        return typeof value !== "undefined" && value !== null;
    }

    export interface IHashable {
        /**
         * This function will return the string representation of the complex object
         * @type {function}
         */
        toString(): string;
    }

    /**
     * Check if given value is defined. Returns true when defined, otherwise false.
     *
     * @export
     * @param {any} value - Value to check for defined
     * @return {boolean}
     */
    export function isDefined(value: any): boolean {
        return (typeof value !== "undefined");
    }

    /**
     * Check if a given value is a valid key value pair
     *
     * @export
     * @param potentialKeyValuePair
     * @return {boolean}
     */
    export function isKeyValuePair<K, V>(potentialKeyValuePair: { key: K, value: V },
        isKey: (key: K) => boolean,
        isValue: (value: V) => boolean): boolean {
        return Utility.isNonNullObject(potentialKeyValuePair)
            && isKey(potentialKeyValuePair.key)
            && isValue(potentialKeyValuePair.value);
    }

    /**
     * Check if given value is a non null object. Returns true when value is non null , otherwise false.
     *
     * @export
     * @param {any} value - Value to check for non null
     * @return {boolean}
     */
    export function isNonNullObject(value: any): boolean {
        return value && typeof value === "object";
    }

    /**
     * Check if given value is not null. Returns true when not null, otherwise false.
     *
     * @export
     * @param {any} value - Value to check for not null
     * @return {boolean}
     */
    export function isNotNull(value: any): boolean {
        return value !== null;
    }

    /**
     * Check if given value is a a non-null or non-empty string. Returns true when it is, otherwise false.
     *
     * @export
     * @param {string} value - Value to check for string
     * @return {boolean}
     */
    export function isNullOrWhiteSpace(value: string): boolean {
        return !isString(value) || value.trim() === "";
    }

    /**
     * Converts string to number
     *
     * @export
     * @param {string} value - string to convert to a number
     * @param {number} defaultValue - number to return if there is an error
     * @return {number}
     */
    export function toNumber(value: string, defaultValue = -1): number {
        if (!value) {
            return defaultValue;
        }

        const result = _toNumber(value);
        if (!isNaN(result)) {
            return result;
        } else {
            return defaultValue;
        }
    }

    /**
     * Check if given value is an array with content in it.
     * Returns true when value is an array with content, otherwise false.
     *
     * @export
     * @param {any[]} value - Value to check for array with content
     * @return {boolean}
     */
    export function arrayHasData(value: any[]): boolean {
        if (isNotNullOrUndefined(value)) {
            return ((value.constructor === Array) && (value.length > 0));
        }

        return false;
    }

    /**
     * Check if given value is a string with content in it.
     * Returns true when value is a string with content, otherwise false.
     *
     * @export
     * @param {string} value - Value to check for string with content
     * @return {boolean}
     */
    export function stringHasData(value: string): boolean  {
        return (isString(value) && (value.trim().length > 0));
    }

    /**
     * Convert object to string representation for it's values.
     * @param {object} object - The object to convert
     * @returns {string}
     */
    export function objectToString(object: object): string {
        let value = "";
        let firstRun = true;

        if (typeof object !== "object") {
            return value;
        }

        for (const attribute in object) {
            if (typeof object[attribute] === "object") {
                value += firstRun ? `${objectToString(object[attribute])}` : `-${objectToString(object[attribute])}`;
            } else {
                value += firstRun ? `${object[attribute]}` : `-${object[attribute]}`;
            }

            firstRun = false;
        }

        return value;
    }

    /**
     * Helper function to replace hyphens with empty strings
     *
     * @param {string} input - Input string
     * @returns {string}
     */
    export function removeHyphens(input: string): string {
        if (!input) {
            return;
        }
        return input.replace(/-/g, "");
    }
}